import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/feature/SchemaOrg';
import HeroBanner from '@/pages/home/components/HeroBanner';
import CategoryTabs from '@/pages/home/components/CategoryTabs';
import DramaGrid from '@/pages/home/components/DramaGrid';
import { dramas, categories } from '@/mocks/dramas';

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredDramas = useMemo(() => {
    if (activeCategory === 'all') return dramas;
    return dramas.filter((d) => d.categorySlug === activeCategory);
  }, [activeCategory]);

  const activeCategoryName = categories.find((c) => c.slug === activeCategory)?.name || '全部';

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: '萝卜影视 - 电视剧全集在线观看',
          description: '萝卜影视收录电视剧全集、连续剧完整版及电视剧大结局，免费在线观看。',
          url: 'https://www.luobotv.com/',
          isPartOf: {
            '@type': 'WebSite',
            name: '萝卜影视',
            url: 'https://www.luobotv.com/',
          },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <HeroBanner />

          {/* Category Section */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-1 h-6 rounded-full bg-primary-500"></div>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900">
                  分类浏览
                </h2>
              </div>
              <p className="text-sm text-foreground-500 mt-1 ml-4">按类型筛选你喜欢的电视剧</p>
            </div>
            <CategoryTabs active={activeCategory} onChange={setActiveCategory} />
          </section>

          {/* Drama Grid */}
          <section className="w-full px-4 md:px-6 pb-14">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-1 h-6 rounded-full bg-accent-500"></div>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900">
                  {activeCategoryName}电视剧
                </h2>
              </div>
              <p className="text-sm text-foreground-500 mt-1 ml-4">
                共 {filteredDramas.length} 部作品等你来看
              </p>
            </div>
            <DramaGrid dramas={filteredDramas} />
          </section>

          {/* Hot Ranking */}
          <section className="w-full px-4 md:px-6 py-14 bg-background-100">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-1 h-6 rounded-full bg-accent-500"></div>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900">
                  热门排行榜
                </h2>
              </div>
              <p className="text-sm text-foreground-500 mt-1 ml-4">高分好剧，不容错过</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[...dramas]
                .sort((a, b) => b.rating - a.rating)
                .slice(0, 6)
                .map((drama, index) => (
                  <a
                    key={drama.id}
                    href={`/drama/${drama.id}`}
                    className="flex items-center gap-4 p-4 rounded-lg bg-background-50 hover:bg-background-200/50 transition-colors cursor-pointer group"
                  >
                    <span
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                        index < 3
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-200 text-foreground-600'
                      }`}
                    >
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-sm text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {drama.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
                        <span className="flex items-center gap-0.5">
                          <i className="ri-star-fill text-accent-500"></i>
                          {drama.rating}
                        </span>
                        <span>{drama.category}</span>
                        <span>{drama.updateInfo}</span>
                      </div>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-primary-500 transition-colors"></i>
                  </a>
                ))}
            </div>
          </section>

          {/* Features */}
          <section className="w-full px-4 md:px-6 py-14">
            <div className="mb-10 text-center">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                为什么选择萝卜影视
              </h2>
              <p className="text-sm text-foreground-500">为您打造极致的追剧体验</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {[
                { icon: 'ri-movie-line', title: '海量资源', desc: '收录全网热门电视剧全集与完整版' },
                { icon: 'ri-hd-line', title: '高清画质', desc: '支持高清播放，画质清晰不模糊' },
                { icon: 'ri-speed-up-line', title: '极速加载', desc: '不缓冲不卡顿，顺畅观影体验' },
                { icon: 'ri-device-line', title: '多端适配', desc: '电脑、平板、手机都能畅快追剧' },
              ].map((feature) => (
                <div key={feature.title} className="text-center p-6 rounded-lg bg-background-100">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className={`${feature.icon} text-primary-500 text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-base text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full px-4 md:px-6 py-14 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <div className="mb-10 text-center">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                  常见问题
                </h2>
              </div>
              <div className="space-y-3">
                {[
                  { q: '萝卜影视是否免费？', a: '萝卜影视所有电视剧全集与连续剧完整版均免费在线观看，无需付费，无需会员。' },
                  { q: '需要下载APP才能观看吗？', a: '不需要。萝卜影视支持网页端直接在线观看，无需下载任何软件或插件。' },
                  { q: '电视剧更新速度如何？', a: '我们持续收录最新电视剧全集和连续剧完整版，热门剧集上线后在第一时间更新，确保您能追到大结局。' },
                  { q: '支持哪些设备观看？', a: '萝卜影视支持电脑、平板、手机等各类设备，只要有浏览器就能在线观看电视剧全集。' },
                ].map((faq, index) => (
                  <details key={index} className="group bg-background-50 rounded-lg">
                    <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-sm text-foreground-900">
                      {faq.q}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <p className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}